/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// Backdrop click-to-close; real keyboard support (Escape, focus) is wired
// via the effects below, the linter just can't see it statically.
/* eslint-disable react/prop-types */
// product is the shared catalog shape used throughout src/lib/db.js, not
// re-declared per component.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { PRODUCT_CATEGORIES, addProduct, updateProduct } from "lib/db";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

function blankState(product) {
  const knownCategory = product && PRODUCT_CATEGORIES.includes(product.type);
  let type = PRODUCT_CATEGORIES[0];
  if (product) type = knownCategory ? product.type : "Other";

  return {
    title: product?.title || "",
    type,
    customType: product && !knownCategory ? product.type : "",
    packaging: product?.packaging?.length ? [...product.packaging] : [""],
    imageFile: null,
    imagePreview: product?.image_url || "",
    removeImage: false,
  };
}

export default function ProductFormModal({ open, product, onClose, onSaved }) {
  const isEditing = Boolean(product);
  const [form, setForm] = useState(() => blankState(product));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);

  let submitLabel = "Add Product";
  if (isSubmitting) submitLabel = "Saving…";
  else if (isEditing) submitLabel = "Save Changes";

  useEffect(() => {
    if (open) {
      setForm(blankState(product));
      setFormError("");
      setTimeout(() => titleRef.current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFormError("Please choose an image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError("That image is larger than 8MB — please choose a smaller file.");
      return;
    }
    setFormError("");
    const previewUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, imageFile: file, imagePreview: previewUrl, removeImage: false }));
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, imageFile: null, imagePreview: "", removeImage: true }));
  };

  const updatePackaging = (index, value) => {
    setForm((prev) => ({
      ...prev,
      packaging: prev.packaging.map((p, i) => (i === index ? value : p)),
    }));
  };

  const addPackagingRow = () => {
    setForm((prev) => ({ ...prev, packaging: [...prev.packaging, ""] }));
  };

  const removePackagingRow = (index) => {
    setForm((prev) => ({
      ...prev,
      packaging: prev.packaging.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const type = (form.type === "Other" ? form.customType : form.type).trim();
    const packaging = form.packaging.map((p) => p.trim()).filter(Boolean);

    if (!title) {
      setFormError("Product name is required.");
      return;
    }
    if (!type) {
      setFormError("Category is required.");
      return;
    }

    setIsSubmitting(true);
    setFormError("");
    try {
      const payload = {
        title,
        type,
        packaging,
        imageFile: form.imageFile,
        removeImage: form.removeImage,
      };
      const saved = isEditing
        ? await updateProduct(product.id, payload)
        : await addProduct(payload);
      toast.success(isEditing ? "Product updated." : "Product added.");
      onSaved(saved);
    } catch (err) {
      setFormError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
      onClick={() => !isSubmitting && onClose()}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="product-modal-title" className="font-display text-xl font-semibold text-slate-800">
            {isEditing ? "Edit Product" : "Add Product"}
          </h2>
          <button
            type="button"
            onClick={() => !isSubmitting && onClose()}
            aria-label="Close"
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Image uploader */}
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Product Image</span>
            <div
              className="flex items-center gap-4 rounded-xl border border-dashed border-slate-300 p-4 transition-colors hover:border-maroon-300"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                {form.imagePreview ? (
                  <img src={form.imagePreview} alt="Product preview" className="h-full w-full object-contain" />
                ) : (
                  <svg className="h-8 w-8 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    {form.imagePreview ? "Change image" : "Upload image"}
                  </button>
                  {form.imagePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">JPG, PNG or WebP. Drag &amp; drop, or click to browse.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <label className="block" htmlFor="product-title">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Product Name</span>
            <input
              id="product-title"
              ref={titleRef}
              type="text"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Red Chilly Sauce"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
            />
          </label>

          {/* Category */}
          <div>
            <label className="block" htmlFor="product-type">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Category</span>
              <select
                id="product-type"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value="Other">Other (new category)</option>
              </select>
            </label>
            {form.type === "Other" && (
              <input
                type="text"
                value={form.customType}
                onChange={(e) => setForm((prev) => ({ ...prev, customType: e.target.value }))}
                placeholder="Enter new category name"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
              />
            )}
          </div>

          {/* Packaging */}
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Packaging Options</span>
            <div className="space-y-2">
              {form.packaging.map((pack, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={pack}
                    onChange={(e) => updatePackaging(index, e.target.value)}
                    placeholder="e.g. 200g x 48pet"
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
                  />
                  <button
                    type="button"
                    onClick={() => removePackagingRow(index)}
                    aria-label="Remove packaging option"
                    className="shrink-0 rounded-lg border border-slate-200 px-3 text-slate-400 hover:bg-slate-50 hover:text-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addPackagingRow}
              className="mt-2 text-sm font-medium text-maroon-700 hover:underline"
            >
              + Add pack size
            </button>
          </div>

          {formError && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </p>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-maroon-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-maroon-800 disabled:opacity-60"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
